import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useTranslation, SUPPORTED_LANGUAGES } from '../../context/TranslationContext';
import { DropdownPortal } from './DropdownPortal';

export function LanguageSelector() {
  const { currentLanguage, changeLanguage, isDisabled } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const triggerRef = useRef(null);

  // Hide message after 3 seconds
  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
        setMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const currentLang = SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES.en;

  const handleLanguageSelect = (code) => {
    setIsOpen(false);
    
    if (code === 'en') {
      // English is always available
      return;
    }
    
    // Show "coming soon" message for other languages
    const result = changeLanguage(code);
    if (!result.success && result.message) {
      setMessage(result.message);
      setShowMessage(true);
    }
  };

  const closeDropdown = () => setIsOpen(false);

  return (
    <div className="relative">
      {/* Coming soon message */}
      {showMessage && (
        <div className="absolute -top-10 right-0 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap z-[10000] shadow-lg">
          {message}
        </div>
      )}

      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLang.flag}</span>
        <span className="uppercase font-medium">{currentLanguage}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <DropdownPortal 
        isOpen={isOpen} 
        onClose={closeDropdown}
        triggerRef={triggerRef}
        align="right"
      >
        <div 
          className="dropdown-menu w-48"
          role="listbox"
          aria-label="Select language"
          data-testid="language-dropdown-menu"
        >
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageSelect(code)}
              className={`dropdown-menu-item ${currentLanguage === code ? 'active' : ''}`}
              role="option"
              aria-selected={currentLanguage === code}
              data-testid={`language-option-${code}`}
            >
              <span className="text-lg flex-shrink-0">{lang.flag}</span>
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{lang.native}</span>
                <span className="block text-xs text-slate-500 truncate">
                  {lang.name}
                  {code !== 'en' && isDisabled && (
                    <span className="text-amber-600 ml-1">(Coming soon)</span>
                  )}
                </span>
              </div>
              {currentLanguage === code && (
                <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </DropdownPortal>
    </div>
  );
}

export default LanguageSelector;
